import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import { getAgentLight, searchAgents } from 'app/api/agentApi';

interface PersonProps {
  id?: any;
  value?: number;
  onPersonChanged: any;
  isDeletable?: boolean;
  readonly?: boolean;
  showCareer?: boolean;
  selectedCareerType?: number;
  onCareerChanged?: any;
}

const AgentWidget = ({
  id,
  value,
  onPersonChanged,
  isDeletable,
  readonly,
  selectedCareerType,
  onCareerChanged,
  showCareer = true,
}: PersonProps) => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [persons, setPersons] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<string>('');
  const [visibleSearchDialog, setVisibleSearchDialog] = useState(false);
  const [visibleChooseCareer, setVisibleChooseCareer] = useState(false);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      if (value) {
        getAgentLight(value).then(response => {
          // setPersons([{ label: buildLabel(response.data), value: response.data.id }]);
          setSelectedPerson(response.data);
        });
      }
    }
  }, [firstLoad]);

  const fetchData = async (pageNumber: number, filterValue: string = '') => {
    setLoading(true);
    try {
      let params = '';
      if (filterValue) {
        params = '?query=' + filterValue;
      }

      const response = await searchAgents(params);

      setPersons(response.data.content); // Reset options on new filter
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter) {
      const delayDebounceFn = setTimeout(() => {
        fetchData(0, filter);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [filter]);

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setVisibleSearchDialog(false)}
      />
    </div>
  );

  const has2Careers = data => {
    return data.oldCareer && data.newCareer;
  };

  const handleSelectPerson = (rowData: any) => {
    setSelectedPerson(rowData);
    onPersonChanged(rowData.id);

    if (has2Careers(rowData)) {
      setVisibleChooseCareer(true);
    } else {
      setVisibleSearchDialog(false); // Close the modal after selection
    }
  };

  const handleSelectCareerType = type => {
    if (type) {
      onCareerChanged(type);
      setVisibleChooseCareer(false);
      setVisibleSearchDialog(false);
    }
  };

  // Template for the "Select" button in each row
  const selectButtonTemplate = (rowData: any) => (
    <Button style={{ backgroundColor: '#e31818' }} type="button" size="small" label="Vybrať" onClick={() => handleSelectPerson(rowData)} />
  );

  const onOpenSearchModal = () => {
    setFilter('');
    setPersons([]);
    setVisibleSearchDialog(true);
  };

  const onDeleteAgent = () => {
    setSelectedPerson(null);
    onPersonChanged(null);
  };

  const formatCareerName = () => {
    if (selectedCareerType) {
      if (selectedCareerType === 1 || !selectedPerson?.newCareer) {
        return selectedPerson?.oldCareer?.positionNr + ' - ' + selectedPerson?.oldCareer?.position;
      } else if (selectedCareerType === 2 || !selectedPerson?.oldCareer) {
        return selectedPerson?.newCareer?.positionNr + ' - ' + selectedPerson?.newCareer?.position;
      }
    }

    return '';
  };

  return (
    <div className="p-inputgroup flex-1">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          width: '100%',
        }}
      >
        {/* Left section with text info */}
        <div onClick={!readonly ? onOpenSearchModal : null}>
          {selectedPerson?.name && (
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {selectedPerson?.name} ({selectedPerson?.ico})
            </div>
          )}
          {!selectedPerson?.name && (
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              {selectedPerson?.firstName} {selectedPerson?.lastName}
            </div>
          )}
          <div style={{ margin: '5px 0' }}>{selectedPerson?.birthNr}</div>
          <div>{selectedPerson?.email}</div>
          {showCareer && <div>{formatCareerName()}</div>}
        </div>

        {/* Right section with button */}
        {!readonly && selectedPerson?.id && (
          <div style={{ display: 'flex' }}>
            <Divider layout="vertical" />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <Button
                label="Nájsť"
                type="button"
                onClick={onOpenSearchModal}
                size="small"
                icon="pi pi-search"
                style={{ backgroundColor: '#e31818', height: '30px', marginBottom: '5px' }}
              />
              {isDeletable && (
                <Button
                  label="Zrušiť"
                  type="button"
                  onClick={onDeleteAgent}
                  size="small"
                  severity="danger"
                  icon="pi pi-info-circle"
                  style={{ backgroundColor: 'gray', height: '30px' }}
                />
              )}
            </div>
          </div>
        )}
        {!readonly && !selectedPerson?.id && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              style={{ backgroundColor: '#e31818' }}
              label="Nájsť poradcu"
              type="button"
              onClick={onOpenSearchModal}
              size="small"
              icon="pi pi-search"
            />
          </div>
        )}
      </div>

      <Dialog
        visible={visibleSearchDialog}
        header="Nájsť poradcu"
        style={{ width: '80vw' }}
        footer={dialogFooter}
        onHide={() => setVisibleSearchDialog(false)}
      >
        {/* InputText for user query */}
        <div className="p-field col-12" style={{ width: '100%' }}>
          <InputText
            style={{ width: '100%' }}
            id="query"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Hľadať poradcu"
          />
        </div>

        {/* DataTable to show results */}
        {!visibleChooseCareer && persons.length > 0 && (
          <DataTable stripedRows loading={loading} size="small" lazy value={persons} responsiveLayout="scroll" className="p-mt-4">
            <Column field="firstName" header="Prvé meno" />
            <Column field="lastName" header="Priezvisko" />
            <Column field="name" header="Obchodné meno" />
            <Column field="ico" header="IČO" />
            <Column field="birthNr" header="Rodné číslo" />
            <Column field="email" header="E-mail" />
            <Column field="oldCareer.position" header="Stará kariéra" />
            <Column field="newCareer.position" header="Nová kariéra" />
            <Column body={selectButtonTemplate} header="Akcia" />
          </DataTable>
        )}
        {visibleChooseCareer && (
          <div style={{ justifyContent: 'space-between' }}>
            {selectedPerson?.oldCareer && (
              <Button
                style={{ backgroundColor: '#e31818', marginRight: 20 }}
                label={`Stará kariéra: ${selectedPerson.oldCareer?.positionNr} - ${selectedPerson.oldCareer?.position}`}
                onClick={() => handleSelectCareerType(1)}
              ></Button>
            )}
            {selectedPerson?.newCareer && (
              <Button
                style={{ backgroundColor: '#e31818' }}
                label={`Nová kariéra: ${selectedPerson.newCareer?.positionNr} - ${selectedPerson.newCareer?.position}`}
                onClick={() => handleSelectCareerType(2)}
              ></Button>
            )}
          </div>
        )}

        {/*<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>*/}
        {/*  <Button label="Vytvoriť novú osobu" type="button" onClick={() => onOpenCreateModal(null)} size="large" />*/}
        {/*</div>*/}
      </Dialog>
    </div>
  );
};

export default AgentWidget;
